import { FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { gsap } from "gsap";
import { Text } from "pixi.js";

import { AbstractBitmapText } from "../../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractView } from "../../../../../abstracts/modules/AbstractView";
import { WinType } from "../../../../../abstracts/types/enums";
import { IWinLevel } from "../../../../../abstracts/types/interfaces";
import { fitDisplayObjectToWidth } from "../../../../../abstracts/utils/GraphicUtils";
import {
    fadeOutAndStopBackgroundMusic,
    playSound,
    stopSound,
} from "../../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../../Apocalypse";
import {
    ApocalypseFontsNames,
    ApocalypseSoundNames,
} from "../../../../assetsLoader/ApocalypseAssetsNames";
import { winLevels } from "../../../../config/ApocalypseConfig";
import { playRadioSound } from "../../../Sound/ApocalypseSoundController";
import { ApocalypseFreespinIntroScreen } from "./ApocalypseFreespinIntroScreen";

export class ApocalypseFreespinOutroScreen extends ApocalypseFreespinIntroScreen {
    private totalWinText: AbstractBitmapText;
    private spinsPlayedText: Text;

    constructor(parentContainer: AbstractView, name: string = `ApocalypseFreespinOutroScreen`) {
        super(parentContainer, name);
    }

    protected continueButtonClickCallback() {
        super.continueButtonClickCallback();

        this.removeChild(this.spinsPlayedText);
        this.removeChild(this.totalWinText);
    }

    protected override init(): void {
        super.init();

        this.totalWinText = new AbstractBitmapText({
            text: `0`,
            style: {
                fontName: ApocalypseFontsNames.APOCALYPSE_BLUE,
                fontSize: 24,
            },
        }); // ! asta poate fi folosit ca si abstract counter. vezi mai la finalul jocului cum sta treaba
        this.totalWinText.name = `totalWinText`;
        this.totalWinText.position.set(this.continueButton.x + this.continueButton.width / 2, -208);

        this.spinsPlayedText = new Text(`0`, {
            dropShadow: true,
            dropShadowAngle: 8,
            dropShadowBlur: 4,
            dropShadowDistance: 2,
            fontFamily: `Helvetica`,
            fontWeight: FontWeight.BOLD,
            fontSize: 32,
            align: `left`,
            wordWrap: true,
            wordWrapWidth: 400,
            fill: [`#d19a38`, `#9e450f`],
        });

        this.spinsPlayedText.anchor.set(0.5);
        this.spinsPlayedText.position.set(
            this.continueButton.x + this.continueButton.width / 2,
            103
        );
    }

    public override getGsapAnimation(
        finalWinValue: number = 0,
        winLevel: IWinLevel = winLevels[0],
        isAboveWin: boolean = true
    ): gsap.core.Timeline {
        if (isAboveWin) {
            if (finalWinValue > 0) {
                // playSound({ name: this.getOutroWinSoundName() });
            }
        }

        if (finalWinValue === 0) {
            // playSound({ name: ApocalypseSoundNames.FS_OUTRO_BELOW_STAKE });
        }

        const spinPlayed: number = Apocalypse.model.data.numberOfFreespinsPlayed;

        this.spinsPlayedText.text =
            Apocalypse.apiPlugin.translations.translate(`Spins played: `) + `${spinPlayed}`;

        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                playSound({ name: ApocalypseSoundNames.VAULT_DOOR_CLOSE });

                fadeOutAndStopBackgroundMusic(1);

                playRadioSound();
            },
            onComplete: () => {
                this.addChild(this.continueButton);
                this.addChild(this.spinsPlayedText);
            },
        });

        this.removeChild(this.customText);

        const finalValueToString: string = finalWinValue === 0 ? `` : finalWinValue.toFixed(2);

        tl.add(
            this.mainSpine.getSpineTween(
                `intro`,
                false,
                () => {
                    this.totalWinText.y = -208;
                    if (isAboveWin) {
                        this.totalWinText.updateValue(``);
                    }
                    fitDisplayObjectToWidth(this.totalWinText, 700);

                    if (isAboveWin) {
                        this.addChild(this.totalWinText);
                    }

                    //         fadeAndStopSound({ name: this.getBackgroundMusicName(), to: 0, duration: 0.5 });
                },
                null,
                () => {
                    if (!isAboveWin) {
                        this.addChild(this.totalWinText);
                    }
                }
            )
        );

        // ! add apocalypseCounter - good for everything ! ;)
        if (finalWinValue > 0) {
            if (isAboveWin) {
                const counterObj = { value: 0 };

                let duration: number = winLevel.animationDuration;
                if (winLevel.winType === WinType.BIG) {
                    duration = 0.01;
                }

                const updateValueTween = gsap.to(counterObj, {
                    value: finalValueToString,
                    duration: duration,
                    onUpdate: () => {
                        this.totalWinText.updateValue(counterObj.value.toFixed(2));
                        fitDisplayObjectToWidth(this.totalWinText, 700);
                    },
                    onStart: () => {
                        if (isAboveWin) {
                            if (
                                winLevel.winType === WinType.MEDIUM ||
                                winLevel.winType === WinType.LARGE
                            ) {
                                playSound({
                                    name: ApocalypseSoundNames.WIN_COUNTUP,
                                    repeat: 100,
                                });
                            }
                        }
                    },
                });

                tl.add(updateValueTween, 0.76);

                if (winLevel.winType !== WinType.BIG) {
                    const highlightTween = gsap.to(this.totalWinText, {
                        duration: 0.1,
                        pixi: {
                            colorize: `white`,
                            colorizeAmount: 1,
                            brightness: 2,
                            scale: `+=0.1`,
                        },
                        yoyo: true,
                        repeat: 1,
                        repeatDelay: 0.1,
                        onUpdate: () => {
                            fitDisplayObjectToWidth(this.totalWinText, 720);
                        },
                        onStart: () => {
                            if (isAboveWin && winLevel.winType !== WinType.BIG) {
                                switch (winLevel.winType) {
                                    case WinType.MEDIUM:
                                        playSound({ name: ApocalypseSoundNames.WIN_MEDIUM });
                                        break;
                                    case WinType.LARGE:
                                        playSound({ name: ApocalypseSoundNames.WIN_LARGE });
                                        break;
                                    case WinType.SMALL:
                                        playSound({ name: ApocalypseSoundNames.WIN_SMALL });
                                        break;

                                    default:
                                        playSound({ name: ApocalypseSoundNames.WIN_TINY });
                                        break;
                                }

                                stopSound({
                                    name: ApocalypseSoundNames.WIN_COUNTUP,
                                });
                            }
                        },
                    });

                    tl.add(highlightTween, `>-=0.1`);
                }

                const moveTotalText = gsap.to(this.totalWinText, {
                    duration: 0.2,
                    y: -238,
                });

                tl.add(moveTotalText, `<-=${winLevel.winType === WinType.BIG ? 0.1 : 0}`);
            } else {
                this.totalWinText.y = -238;
                this.totalWinText.updateValue(finalWinValue.toFixed(2).toString());
            }
        }

        return tl;
    }

    protected getBackgroundMusicName(): string {
        return ApocalypseSoundNames.FREESPIN_MUSIC;
    }

    protected addContinueButton() {
        super.addContinueButton();
    }

    // protected getOutroWinSoundName(): string {
    //     return ApocalypseSoundNames.FS_OUTRO_WIN;
    // }
}
